import React, { DependencyList, useState } from 'react'
import urlJoin from 'url-join'
import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'

import useAsyncEffect from './useAsyncEffect'
import { ResponseList } from '../utils/models'
import goToLoginPage from '../utils/goToLoginPage'

const API_URL = process.env.GATSBY_API_URL || 'http://localhost:8000' //window.location.origin

export type ApiErrorCode = 'TokenExpiredError' | 'Error'

export class ApiError extends Error {
  public code: ApiErrorCode

  public status: number

  constructor(code: ApiErrorCode, status: number, message: string) {
    super(message)

    this.code = code
    this.status = status
  }
}

export type FetchResponse<T> = ResponseList<T> | T | undefined

export type HookResult<T> = [boolean, T, ApiError | null, React.Dispatch<React.SetStateAction<T | undefined>>]

function getApiError(error: AxiosError): ApiError {
  const data = error.response?.data as ApiError

  return {
    status: error.response?.status || 500,
    code: data?.code || 'unknown',
    name: data?.code || 'unknown',
    message: data?.message || 'unknown error',
  }
}
export async function fetchWithTimeout<T>(
  path: string,
  options: AxiosRequestConfig = {},
  timeout = 40000,
): Promise<AxiosResponse<T>> {
  const cancelToken = axios.CancelToken
  const source = cancelToken.source()
  const id = setTimeout(() => {
    console.info(`Request ${path} cancelled after exceeding 20s.`)
    source.cancel()
  }, timeout)
  const response = await axios.request({
    url: urlJoin(API_URL, path),
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
    cancelToken: source.token,
  })

  clearTimeout(id)

  return response
}

export async function betterFetch<T>(path: string, options: AxiosRequestConfig = {}, timeout = 20000): Promise<T> {
  try {
    if (!timeout) {
      const response = await axios.request({
        url: urlJoin(API_URL, path),
        headers: {
          'Content-Type': 'application/json',
        },
        ...options,
      })
      return response.data as T
    }

    const response = await fetchWithTimeout(path, options, timeout)
    return response.data as T
  } catch (error) {
    if ((error as ApiError).status === 401) {
      goToLoginPage()
    }

    throw error
  }
}

function useFetch<T>(path: string, options: AxiosRequestConfig = {}, inputs: DependencyList = []): HookResult<T> {
  const [isLoading, setLoading] = useState(true)
  const [data, setData] = useState<T>()
  const [error, setError] = useState<ApiError | null>(null)

  useAsyncEffect(async () => {
    try {
      const response = await fetchWithTimeout(path, options)

      setData(response.data as T)
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        const axiosError = getApiError(error)

        setError(getApiError(error))

        if (axiosError.status === 401) {
          goToLoginPage()
        }
      } else {
        setError(error)
      }
    } finally {
      setLoading(false)
    }
  }, inputs)

  return [isLoading, data as T, error, setData]
}

export default useFetch
